import { useCallback, useEffect, useState } from "react";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import {
  Alert,
  Box,
  Button,
  Chip,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { notifyError, notifySuccess } from "../../../components/ToastContainer/Toast/Toast";
import { useAppSelector } from "../../../Redux/hooks";
import {
  approvePartialRefund,
  getRefunds,
  RefundFilters,
  RefundRow,
  RefundsResponse,
} from "../../../service/BuyerConfiguration/Refunds/refundService";

/**
 * Refunds.
 *
 * Every refund — to the customer's Tazty Wallet or back to the original
 * payment — with the item-GST choice, who issued it and why. Read-only:
 * refunds are issued from Order History, so there is one place to issue them
 * and one place to review them.
 */

const inr = (n: number | null | undefined) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MODE_LABEL: Record<string, string> = {
  wallet: "Wallet (instant)",
  normal: "Normal (5–7 days)",
  instant_company: "Instant — Tazty pays ₹9",
  instant_buyer: "Instant — customer pays ₹9",
};

const STATUS_COLOR: Record<string, "success" | "warning" | "error" | "info" | "default"> = {
  processed: "success",
  pending: "warning",
  failed: "error",
  awaiting_approval: "info",
  processing: "warning",
};

const STATUS_LABEL: Record<string, string> = {
  awaiting_approval: "Awaiting approval",
  processing: "Processing",
  pending: "Pending",
  processed: "Processed",
  failed: "Failed",
};

type ApproveMode = "wallet" | "normal" | "instant_company" | "instant_buyer";
const INSTANT_FEE = 9;

const PAGE_SIZE = 25;

/** A held partial refund: destination, mode and GST are chosen at approval. */
const isUndecided = (r: RefundRow) =>
  r.status === "awaiting_approval" || r.status === "processing";

const Refunds = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = { ...dataGridCustomizations };
  const [filters, setFilters] = useState<RefundFilters>({});
  const [page, setPage] = useState(0);
  const [data, setData] = useState<RefundsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const adminEmail = useAppSelector((state) => state.roleAndPermission.email);
  const [approving, setApproving] = useState<RefundRow | null>(null);
  const [approveMode, setApproveMode] = useState<ApproveMode>("wallet");
  const [approveGst, setApproveGst] = useState(true);
  const [approveSaving, setApproveSaving] = useState(false);
  // Why this refund is being approved - required, saved with the refund.
  const [approveReason, setApproveReason] = useState("");
  const approveReasonOk = approveReason.trim().length >= 5;

  const openApprove = (row: RefundRow) => {
    setApproveMode("wallet");
    setApproveGst(true);
    setApproveReason("");
    setApproving(row);
  };

  // Until approval, refund_amount is the with-GST figure and tax_value the
  // item GST inside it (the Buyer API recomputes the same way).
  const approvePayout = (row: RefundRow | null) => {
    if (!row) return 0;
    const withGst = Math.round(Number(row.refund_amount) * 100);
    const tax = Math.min(withGst, Math.round(Number(row.tax_value || 0) * 100));
    let p = approveGst ? withGst : withGst - tax;
    if (approveMode === "instant_buyer") p = Math.max(0, p - INSTANT_FEE * 100);
    return p / 100;
  };

  const submitApprove = async () => {
    if (!approving || !approveReasonOk) return;
    setApproveSaving(true);
    try {
      const res: any = await approvePartialRefund(approving.id, {
        mode: approveMode,
        include_gst: approveGst,
        approved_by: adminEmail || undefined,
        reason: approveReason.trim(),
      });
      notifySuccess(
        res?.duplicate
          ? "Already approved - nothing was paid twice"
          : `Refund of ${inr(res?.refund_amount ?? approvePayout(approving))} approved`,
      );
      setApproving(null);
      load(filters, page);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not approve the refund");
    } finally {
      setApproveSaving(false);
    }
  };

  const load = useCallback(async (f: RefundFilters, p: number) => {
    setLoading(true);
    try {
      setData(await getRefunds({ ...f, page: p + 1, limit: PAGE_SIZE }));
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Failed to load refunds");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced so typing an order number does not fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => load(filters, page), 350);
    return () => clearTimeout(t);
  }, [filters, page, load]);

  const setFilter = (patch: Partial<RefundFilters>) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const columns: GridColDef<RefundRow>[] = [
    {
      field: "created_at",
      headerName: "When",
      width: 160,
      valueGetter: (_v, r) => new Date(r.created_at).toLocaleString("en-IN"),
    },
    { field: "order_number", headerName: "Order", width: 190 },
    {
      field: "refund_type",
      headerName: "Type",
      width: 150,
      renderCell: (p) =>
        p.row.refund_type === "partial" ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Unavailable items${p.row.items?.length ? ` (${p.row.items.length})` : ""}`}
          />
        ) : (
          "Whole order"
        ),
    },
    {
      field: "customer",
      headerName: "Customer",
      width: 180,
      valueGetter: (_v, r) =>
        [r.customer_name, r.customer_phone].filter(Boolean).join(" · ") || "—",
    },
    { field: "paid_amount", headerName: "Paid", width: 110, valueGetter: (_v, r) => inr(r.paid_amount) },
    {
      field: "refund_amount",
      headerName: "Refunded",
      width: 120,
      renderCell: (p) => <b>{inr(p.row.refund_amount)}</b>,
    },
    {
      field: "destination",
      headerName: "To",
      width: 130,
      renderCell: (p) =>
        isUndecided(p.row) ? (
          "—"
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color={p.row.destination === "wallet" ? "success" : "default"}
            label={p.row.destination === "wallet" ? "Tazty Wallet" : "Original payment"}
          />
        ),
    },
    { field: "mode", headerName: "Mode", width: 190, valueGetter: (_v, r) => (isUndecided(r) ? "—" : MODE_LABEL[r.mode] || r.mode) },
    {
      field: "gst_refunded",
      headerName: "Item GST",
      width: 150,
      valueGetter: (_v, r) =>
        isUndecided(r)
          ? "—"
          : r.gst_refunded
          ? "Refunded"
          : `Kept ${r.tax_value !== null ? inr(r.tax_value) : ""}`.trim(),
    },
    {
      field: "status",
      headerName: "Status",
      width: 115,
      renderCell: (p) => (
        <Chip
          size="small"
          color={STATUS_COLOR[p.row.status] || "default"}
          label={STATUS_LABEL[p.row.status] || p.row.status}
        />
      ),
    },
    {
      field: "approve",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (p) =>
        p.row.refund_type === "partial" &&
        (p.row.status === "awaiting_approval" || p.row.status === "failed") ? (
          <Button size="small" variant="contained" onClick={() => openApprove(p.row)}>
            Approve
          </Button>
        ) : null,
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 220,
      // Full text on hover; long reasons are cut in the cell.
      renderCell: (p) =>
        p.row.reason ? (
          <span title={p.row.reason} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.row.reason}
          </span>
        ) : (
          <span style={{ opacity: 0.5 }}>—</span>
        ),
    },
    { field: "initiated_by", headerName: "By", width: 170 },
  ];

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">Refunds</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Every refund, to the Tazty Wallet or the original payment. Issue refunds from Order
            History.
          </Typography>
        </Box>

        {!!data?.summary?.awaiting && (
          <Alert
            severity={data.summary.awaiting_overdue ? "warning" : "info"}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setFilter({ status: "awaiting_approval" })}>
                Show
              </Button>
            }
          >
            {data.summary.awaiting} refund{data.summary.awaiting > 1 ? "s" : ""} for unavailable items
            {data.summary.awaiting > 1 ? " are" : " is"} waiting for your approval
            {data.summary.awaiting_overdue
              ? ` - ${data.summary.awaiting_overdue} for more than 24 hours`
              : ""}
            .
          </Alert>
        )}

        {data?.summary && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                To Tazty Wallet
              </Typography>
              <Typography variant="h6">{inr(data.summary.to_wallet)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                To original payment
              </Typography>
              <Typography variant="h6">{inr(data.summary.to_source)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Item GST kept
              </Typography>
              <Typography variant="h6">{inr(data.summary.gst_withheld)}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Failed
              </Typography>
              <Typography variant="h6" color={data.summary.failed ? "error" : undefined}>
                {data.summary.failed}
              </Typography>
            </Paper>
          </Stack>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Order number"
            value={filters.order_number || ""}
            onChange={(e) => setFilter({ order_number: e.target.value })}
          />
          <TextField
            size="small"
            label="Customer phone"
            value={filters.phone || ""}
            onChange={(e) => setFilter({ phone: e.target.value })}
          />
          <TextField
            size="small"
            label="Reason contains"
            value={filters.reason || ""}
            onChange={(e) => setFilter({ reason: e.target.value })}
          />
          <TextField
            select
            size="small"
            label="To"
            value={filters.destination || ""}
            onChange={(e) => setFilter({ destination: (e.target.value || undefined) as any })}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="wallet">Tazty Wallet</MenuItem>
            <MenuItem value="source">Original payment</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status || ""}
            onChange={(e) => setFilter({ status: (e.target.value || undefined) as any })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="awaiting_approval">Awaiting approval</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="processed">Processed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from || ""}
            onChange={(e) => setFilter({ from: e.target.value || undefined })}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to || ""}
            onChange={(e) => setFilter({ to: e.target.value || undefined })}
          />
        </Stack>

        <DataGrid
          rows={data?.items ?? []}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
          autoHeight
          paginationMode="server"
          rowCount={data?.pagination?.total ?? 0}
          pageSizeOptions={[PAGE_SIZE]}
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          onPaginationModelChange={(m) => setPage(m.page)}
          localeText={{ noRowsLabel: "No refunds match these filters" }}
        />
      </Box>

      {/* Approve a held partial refund */}
      <Dialog open={!!approving} onClose={() => !approveSaving && setApproving(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve refund · {approving?.order_number}</DialogTitle>
        <DialogContent dividers>
          {approving && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Unavailable items
                </Typography>
                {(approving.items ?? []).map((it) => (
                  <Typography key={it.order_item_id} variant="body2" sx={{ opacity: 0.8 }}>
                    {it.name || "Item"} × {it.quantity} - {inr(it.cancelled_value)}
                  </Typography>
                ))}
                {approving.reason && (
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Store's reason: {approving.reason}
                  </Typography>
                )}
              </Box>
              <RadioGroup value={approveMode} onChange={(e) => setApproveMode(e.target.value as ApproveMode)}>
                <FormControlLabel value="wallet" control={<Radio size="small" />} label="Tazty Wallet - instant, no fee" />
                <FormControlLabel value="normal" control={<Radio size="small" />} label="Original payment - normal (5-7 days, free)" />
                <FormControlLabel value="instant_company" control={<Radio size="small" />} label="Original payment - instant (Tazty pays ₹9)" />
                <FormControlLabel value="instant_buyer" control={<Radio size="small" />} label="Original payment - instant (customer pays ₹9)" />
              </RadioGroup>
              {Number(approving.tax_value || 0) > 0 && (
                <FormControlLabel
                  control={<Switch checked={approveGst} onChange={(e) => setApproveGst(e.target.checked)} />}
                  label={`Refund item GST (${inr(approving.tax_value)})`}
                />
              )}
              <TextField
                required
                multiline
                minRows={2}
                size="small"
                label="Reason for approving this refund"
                placeholder="e.g. Store confirmed items were out of stock"
                value={approveReason}
                onChange={(e) => setApproveReason(e.target.value)}
                inputProps={{ maxLength: 500 }}
                error={approveReason.length > 0 && !approveReasonOk}
                helperText={approveReasonOk ? "Saved with the refund" : "Required - at least 5 characters"}
              />
              <Alert severity="info">
                Customer receives <b>{inr(approvePayout(approving))}</b>
                {approveMode === "wallet" ? " in their Tazty Wallet" : " to their original payment method"}.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproving(null)} disabled={approveSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitApprove} disabled={approveSaving || !approveReasonOk || approvePayout(approving) <= 0}>
            {approveSaving ? "Approving..." : `Approve & refund ${inr(approvePayout(approving))}`}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default Refunds;
